import { useState, useEffect } from "react";
import {
  Box,
  Alert,
  Typography,
  MenuItem,
  SelectChangeEvent,
  LinearProgress,
  Select,
  Grid,
  Paper,
  List,
  ListItemButton,
  ListItemText,
  Divider,
  Chip,
  Button,
  Stack,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useLocationMatching } from "./useLocationMatching";
import { ConfirmMatchDto } from "../../../utilities/apiInstances/location-matching-api";
import { enqueueSnackbar } from "notistack";

interface Relationship {
  id: number | string;
  name: string;
}

// Component used for matching SafeLease locations to Cubby facilities
function LocationMatching() {
  const [selectedRelationshipId, setSelectedRelationshipId] = useState<number | null>(null);
  const [selectedSafeleaseLocationId, setSelectedSafeleaseLocationId] = useState<number | null>(null);
  const [selectedCubbyFacilityId, setSelectedCubbyFacilityId] = useState<number | null>(null);
  const { getUnmatchedLocationsQuery, getUnmatchedRelationshipsQuery, useCubbyFacilitiesQuery, confirmMatchMutation } =
    useLocationMatching();

  const relationships: Relationship[] = getUnmatchedRelationshipsQuery.data?.relationships ?? [];

  // If there are relationships, and no relationship is selected, select the first relationship
  useEffect(() => {
    if (relationships.length && selectedRelationshipId == null) {
      setSelectedRelationshipId(Number(relationships[0].id));
    }

    // Reset selected safelease location and cubby facility when relationship changes
    if (selectedRelationshipId) {
      setSelectedSafeleaseLocationId(null);
      setSelectedCubbyFacilityId(null);
    }
  }, [relationships, selectedRelationshipId]);

  const cubbyFacilitiesQuery = useCubbyFacilitiesQuery(selectedRelationshipId);

  // Returning loader in place of component if locations, relationships or confirm match is loading
  if (getUnmatchedLocationsQuery.isLoading || getUnmatchedRelationshipsQuery.isLoading || confirmMatchMutation.isLoading) {
    return <LinearProgress />;
  }

  if (getUnmatchedLocationsQuery.isError || getUnmatchedRelationshipsQuery.isError || cubbyFacilitiesQuery.isError) {
    return <Alert severity="error">Error Loading Data</Alert>;
  }

  const handleRelationshipChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value;
    setSelectedRelationshipId(value ? Number(value) : null);
  };

  const handleConfirmMatch = async (dto: ConfirmMatchDto) => {
    await confirmMatchMutation.mutateAsync(dto);
    await getUnmatchedLocationsQuery.refetch();
    await getUnmatchedRelationshipsQuery.refetch();
    enqueueSnackbar("Match confirmed", { variant: "success" });
  };

  const mapCubbyFacilities = (cubbyFacilities: any[]) => {
    if (cubbyFacilitiesQuery.isLoading) {
      return <LinearProgress />;
    } else {
      return cubbyFacilities.map((f) => (
        <ListItemButton
          key={f.id}
          selected={f.id === selectedCubbyFacilityId}
          onClick={() => setSelectedCubbyFacilityId(f.id)}
          sx={{
            borderLeft: f.id === selectedCubbyFacilityId ? "4px solid #3781FB" : "4px solid transparent",
            "&.Mui-selected": { bgcolor: "rgba(55,129,251,0.08)" },
            "&.Mui-selected:hover": { bgcolor: "rgba(55,129,251,0.12)" },
          }}
        >
          <ListItemText primary={f.name} secondary={f.address} />
          {f.id === selectedCubbyFacilityId && <CheckCircleIcon color="primary" fontSize="small" />}
        </ListItemButton>
      ));
    }
  };

  const mapUnmatchedLocationsForRelationship = (locations: any[]) => {
    return locations.map((l) => (
      <ListItemButton
        key={l.id}
        selected={l.id === selectedSafeleaseLocationId}
        onClick={() => setSelectedSafeleaseLocationId(l.id)}
        sx={{
          borderLeft: l.id === selectedSafeleaseLocationId ? "4px solid #3781FB" : "4px solid transparent",
          "&.Mui-selected": { bgcolor: "rgba(55,129,251,0.08)" },
          "&.Mui-selected:hover": { bgcolor: "rgba(55,129,251,0.12)" },
        }}
      >
        <ListItemText primary={l.name} secondary={l.address} />
        {l.id === selectedSafeleaseLocationId && <CheckCircleIcon color="primary" fontSize="small" />}
      </ListItemButton>
    ));
  };

  const unmatchedLocationsForRelationship =
    getUnmatchedLocationsQuery.data?.locations?.filter((l) => l.relationshipId === selectedRelationshipId) ?? [];

  const cubbyFacilitiesData = cubbyFacilitiesQuery.data?.matchedFacilities || [];

  const selectedCubbyFacility = selectedCubbyFacilityId ? cubbyFacilitiesData.find((f: any) => f.id === selectedCubbyFacilityId) : null;

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        {getUnmatchedLocationsQuery.data?.locations?.length ?? 0} unmatched cubby locations found across all relationships.
      </Alert>

      <Typography variant="body1" gutterBottom>
        Here you will be able to match cubby facilities to SafeLease locations
      </Typography>

      {relationships.length > 0 && (
        <Select value={selectedRelationshipId?.toString() ?? ""} onChange={handleRelationshipChange} displayEmpty>
          {relationships.map((relationship) => (
            <MenuItem key={relationship.id} value={relationship.id.toString()}>
              {relationship.name}
            </MenuItem>
          ))}
        </Select>
      )}

      {/* Mock matching workspace */}
      <Box sx={{ mt: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={5}>
            <Paper sx={{ p: 2 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="subtitle1">SafeLease locations (unmatched)</Typography>
                <Chip label={unmatchedLocationsForRelationship.length.toString()} size="small" />
              </Stack>
              {selectedSafeleaseLocationId && (
                <Alert severity="info" sx={{ mb: 1 }}>
                  Selected: {unmatchedLocationsForRelationship.find((l) => l.id === selectedSafeleaseLocationId)?.name}
                </Alert>
              )}
              <List dense>{mapUnmatchedLocationsForRelationship(unmatchedLocationsForRelationship)}</List>
            </Paper>
          </Grid>
          <Grid item xs={12} md={2}>
            <Box sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Divider orientation="vertical" flexItem />
            </Box>
          </Grid>
          <Grid item xs={12} md={5}>
            <Paper sx={{ p: 2 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="subtitle1">Cubby facilities (UI)</Typography>
                <Chip label={cubbyFacilitiesData.length.toString()} size="small" />
              </Stack>
              {selectedCubbyFacility && (
                <Alert severity="info" sx={{ mb: 1 }}>
                  Selected: {selectedCubbyFacility.name}, {selectedCubbyFacility.address}
                </Alert>
              )}
              <List dense>{mapCubbyFacilities(cubbyFacilitiesData)}</List>
            </Paper>
          </Grid>
        </Grid>
      </Box>

      <Box sx={{ mt: 2 }}>
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Selected Match
          </Typography>
          {selectedSafeleaseLocationId && selectedCubbyFacilityId ? (
            <Stack direction="row" spacing={2} alignItems="center" sx={{ flexWrap: "wrap" }}>
              <Box sx={{ minWidth: 280 }}>
                <Typography variant="body2" fontWeight={600}>
                  SafeLease Location
                </Typography>
                <Typography variant="body2">
                  {unmatchedLocationsForRelationship.find((l) => l.id === selectedSafeleaseLocationId)?.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {unmatchedLocationsForRelationship.find((l) => l.id === selectedSafeleaseLocationId)?.address}
                </Typography>
              </Box>
              <Box sx={{ minWidth: 280 }}>
                <Typography variant="body2" fontWeight={600}>
                  Cubby Facility
                </Typography>
                <Typography variant="body2">{selectedCubbyFacility?.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {selectedCubbyFacility?.address}
                </Typography>
              </Box>
              <Button
                variant="contained"
                color="success"
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                }}
                onClick={() =>
                  handleConfirmMatch({
                    safeleaseLocationId: selectedSafeleaseLocationId,
                    cubbyFacilityId: selectedCubbyFacilityId,
                  })
                }
              >
                Confirm Match
              </Button>
            </Stack>
          ) : (
            <Alert severity="info">Select a SafeLease location and a Cubby facility to review the selected match.</Alert>
          )}
        </Paper>
      </Box>
    </Box>
  );
}

export default LocationMatching;
